import { useCallback, useEffect, useState } from 'react';
import type { EventSetup, RegistrationRow } from '../api/client.js';
import type { ControllerSession } from '../controller.js';

/** Admin: who has signed up. Speakers wait here for approval and a room. */
export function PeoplePanel({ session }: { session: ControllerSession }) {
  const eventId = session.engine?.record.eventId;
  const [rows, setRows] = useState<RegistrationRow[]>([]);
  const [setup, setSetup] = useState<EventSetup | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!eventId) return;
    void session.client.registrations(eventId).then((r) => setRows(r.registrations)).catch((e: unknown) => setErr(String(e)));
    void session.client.getSetup(eventId).then((r) => setSetup(r.setup)).catch(() => undefined);
  }, [session, eventId]);
  useEffect(load, [load]);
  if (!eventId) return null;

  const act = async (fn: () => Promise<unknown>) => {
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr((e instanceof Error ? e.message : String(e)).replace(/^[A-Z]+ \S+ -> \d+: /, '').replace(/^\{"error":"(.*)"\}$/, '$1'));
    }
    load();
  };
  const students = rows.filter((r) => r.kind === 'student');
  const speakers = rows.filter((r) => r.kind === 'speaker');
  const roomName = (id: string | null) => setup?.rooms.find((r) => r.roomId === id)?.roomName ?? '';
  const q = (r: RegistrationRow, list: EventSetup['studentQuestions'] | undefined) => (list ?? []).map((x) => r.answers[x.id]).filter(Boolean).join(' · ');
  const remove = (r: RegistrationRow) => {
    if (confirm(`Remove ${r.displayName}? Their Zoom link stops working.`)) void act(() => session.client.removeRegistration(eventId, r.id));
  };

  return (
    <div>
      <div className="row">
        <button className="secondary" onClick={load}>Refresh</button>
        <span className="muted">{students.length} students · {speakers.filter((s) => s.status === 'approved').length} speakers approved · {speakers.filter((s) => s.status === 'pending').length} waiting</span>
      </div>
      {err && <div className="banner banner-error" role="alert">{err}</div>}

      <h2>Speakers and mentors</h2>
      {speakers.length === 0 && <p className="muted">Nobody yet. Share the speaker link from the Setup tab.</p>}
      {speakers.length > 0 && (
        <table>
          <thead><tr><th>Name</th><th>Email</th><th>About</th><th>Room</th><th>Status</th><th /></tr></thead>
          <tbody>
            {speakers.map((r) => (
              <tr key={r.id}>
                <td>{r.displayName}</td>
                <td>{r.email}</td>
                <td>{q(r, setup?.speakerQuestions)}</td>
                <td>
                  <select aria-label={`Room for ${r.displayName}`} value={r.roomId ?? ''} onChange={(e) => void act(() => session.client.updateRegistration(eventId, r.id, { roomId: e.target.value || null }))}>
                    <option value="">Choose…</option>
                    {(setup?.rooms ?? []).map((x) => <option key={x.roomId} value={x.roomId}>{x.roomName}</option>)}
                  </select>
                </td>
                <td>{r.status}{r.status === 'approved' && roomName(r.roomId) ? ` · ${roomName(r.roomId)}` : ''}</td>
                <td className="row">
                  {r.status !== 'approved' && <button className="primary" disabled={!r.roomId} onClick={() => void act(() => session.client.updateRegistration(eventId, r.id, { action: 'approve' }))}>Approve</button>}
                  {r.status === 'pending' && <button className="secondary" onClick={() => void act(() => session.client.updateRegistration(eventId, r.id, { action: 'reject' }))}>Decline</button>}
                  <button className="secondary" onClick={() => remove(r)}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Students</h2>
      {students.length === 0 && <p className="muted">Nobody yet. Share the student link from the Setup tab.</p>}
      {students.length > 0 && (
        <table>
          <thead><tr><th>Name in Zoom</th><th>Email</th><th>About</th><th>Joined</th><th /></tr></thead>
          <tbody>
            {students.map((r) => (
              <tr key={r.id}>
                <td>{r.displayName}</td>
                <td>{r.email}</td>
                <td>{q(r, setup?.studentQuestions)}</td>
                <td>{new Date(r.createdAt).toLocaleString()}</td>
                <td><button className="secondary" onClick={() => remove(r)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
